import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { motion, stagger } from 'framer-motion';
import { LanguageSwitcher } from '@/components/language-switcher';
export const Route = createFileRoute('/_layout/powered-by-adora/')({
    component: PoweredByAdoraPage,
});
const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};
function PoweredByAdoraPage() {
    return (<motion.section className="min-h-screen bg-slate-950 text-slate-50" initial="hidden" animate="visible" transition={{ delayChildren: stagger(0.08) }}>
            <div className="mx-auto max-w-5xl px-4 py-8 md:py-12">
                {/* Top bar */}
                <motion.div className="mb-10 flex items-center justify-between" variants={fadeUp}>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-primary font-semibold">
                        Powered by Adora
                    </span>
                    <LanguageSwitcher />
                </motion.div>

                <div className="space-y-16">
                    {/* HERO */}
                    <motion.header className="text-center space-y-5" variants={fadeUp}>
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
                            Offline marketing finally measurable
                        </h1>

                        <p className="mx-auto max-w-2xl text-sm sm:text-base text-slate-300">
                            Adora helps businesses understand how people discover them through menus, packaging,
                            posters, flyers, and physical placements — not just clicks and impressions.
                        </p>

                        <div className="flex justify-center gap-3">
                            <Link to="/sign-up" className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium hover:bg-[#5b5fd0]">
                                Start free
                            </Link>
                            <Link to="/what-is-adora" className="rounded-full border border-slate-700 px-6 py-2.5 text-sm hover:bg-slate-900">
                                What is Adora?
                            </Link>
                        </div>
                    </motion.header>

                    {/* THE BLIND SPOT */}
                    <motion.section className="space-y-6" variants={fadeUp}>
                        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">
                            The offline analytics blind spot
                        </h2>

                        <p className="text-sm text-slate-300 max-w-3xl">
                            Traditional analytics tools are built for ads, websites, and social platforms. They tell you
                            what happens <em>after</em> someone clicks — but not why they clicked.
                        </p>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                                <h3 className="text-sm font-semibold mb-2">What online analytics show</h3>
                                <ul className="text-xs text-slate-300 list-disc pl-4 space-y-1">
                                    <li>Page views and sessions</li>
                                    <li>Clicks and conversions</li>
                                    <li>Traffic sources</li>
                                </ul>
                            </div>

                            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                                <h3 className="text-sm font-semibold mb-2">What they don’t show</h3>
                                <ul className="text-xs text-slate-300 list-disc pl-4 space-y-1">
                                    <li>Which menu, flyer, or label worked</li>
                                    <li>Which physical placement performed best</li>
                                    <li>Why people took action offline</li>
                                </ul>
                            </div>
                        </div>
                    </motion.section>

                    {/* HOW ADORA WORKS */}
                    <motion.section className="space-y-6" variants={fadeUp}>
                        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">How Adora works</h2>

                        <p className="text-sm text-slate-300 max-w-3xl">
                            Adora treats every QR code and short link as part of a campaign — so offline materials
                            become measurable entry points.
                        </p>

                        <div className="grid gap-4 md:grid-cols-3">
                            {[
            {
                title: 'Create a campaign',
                body: 'Each promotion, menu, or flyer becomes its own campaign.',
            },
            {
                title: 'Distribute offline',
                body: 'Use QR codes or short links on physical materials.',
            },
            {
                title: 'See real signals',
                body: 'Track scans, visits, and performance by placement.',
            },
        ].map((item, i) => (<motion.div key={i} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4" whileHover={{ y: -4 }}>
                                    <h3 className="text-sm font-semibold">{item.title}</h3>
                                    <p className="text-xs text-slate-300 mt-1">{item.body}</p>
                                </motion.div>))}
                        </div>
                    </motion.section>

                    {/* WHO IT’S FOR */}
                    <motion.section className="space-y-6" variants={fadeUp}>
                        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">Who Adora is built for</h2>

                        <div className="grid gap-4 md:grid-cols-2">
                            {[
            'Restaurants and cafés',
            'Retail and packaged products',
            'Printers and print shops',
            'Agencies running offline campaigns',
        ].map((label, i) => (<div key={i} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-300">
                                    {label}
                                </div>))}
                        </div>
                    </motion.section>

                    {/* FINAL CTA */}
                    <motion.section className="text-center border-t border-slate-800 pt-8 space-y-4" variants={fadeUp}>
                        <h2 className="text-xl md:text-2xl font-semibold">Measure what actually works offline</h2>
                        <p className="text-sm text-slate-300">Stop guessing. Start tracking real-world campaigns.</p>

                        <Link to="/sign-up" className="inline-flex rounded-full bg-primary px-8 py-2.5 text-sm font-medium hover:bg-[#5b5fd0]">
                            Create free account
                        </Link>

                        <p className="text-[11px] text-slate-500">
                            Already have an account?{' '}
                            <Link to="/sign-in" className="underline text-[#9fa2ff]">
                                Sign in
                            </Link>
                        </p>
                    </motion.section>
                </div>
            </div>
        </motion.section>);
}
